import React, {useState, useEffect, useRef, createRef}  from 'react';
import { MyDownloadsButton, DownloadConfirmation, MenuTabButtons } from 'components/show_drawing_download_section/download_buttons/index.js';
import { ContentPopup } from 'components/ContentPopup.jsx';
import Constants from 'components/show_drawing/Constants.jsx';
import MyEventListener from 'components/common/MyEventListener.jsx'
import Progressbar from 'components/common/Progressbar.jsx'

const LoggedInCanDownload = ( { id, drawing, currentDrawingOptions, currentUserDrawingOptions, advancedOptionsVisible, dimensionTextschecked, redrawInProgress, comboEnabled, formRef } ) => {
  const [showPopUp, setShowPopUp] = useState(false);
  const [pleaseWait, setPleaseWait] = useState(false)
  const [format, setFormat] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const popupRef = createRef();
  const dropdownRef = useRef(null);

  const handleDownloadDXF = (e) => {
    e.preventDefault();
    setFormat( () => 'dxf' )
  }

  const handleDownloadSVG = (e) => {
    e.preventDefault();
    setFormat( () => 'svg' )
  }

  const handleDownloadPDF = (e) => {
    e.preventDefault();
    setFormat( () => 'pdf' )
  }

  const closePopUp = () => {
    if( popupRef.current != undefined) {
      popupRef.current.style.display = "none";
    }
    setShowPopUp(() => false);
    setFormat( () => "" )
    setPleaseWait( () => false )
  }

  const getContent = () => {
    if(pleaseWait) { return <Progressbar />}

    return <DownloadConfirmation
      id={id}
      drawing={drawing}
      currentDrawingOptions={currentDrawingOptions}
      currentUserDrawingOptions={currentUserDrawingOptions}
      advancedOptionsVisible={advancedOptionsVisible}
      fileFormat={format}
      dimensionTextschecked={dimensionTextschecked}
      comboEnabled={comboEnabled}
      formRef={formRef}
    />
  }


  const popupContent = () => {
    let onReady = () => {}
    return <ContentPopup
      contextClass="wrapper"
      myRef={popupRef}
      autoOpen={true}
      content={getContent()}
      cancelButton={false}
      beforeClose={closePopUp}
      onReady={onReady}
      notWide={true}
    />;
  }

  // useEffect
  useEffect( () => {
    if(format == "") { return }

    setShowPopUp( () => true);
  }, [format])
  // useEffect

  MyEventListener([
    {
      key: Constants.drawing_download_started,
      callback: (msg) => {
        if(msg.detail.id == id) {
          setPleaseWait( () => true )
        }
      }
    }, {
      key: Constants.drawing_download_finished,
      callback: (msg) => {
        if(msg.detail.id == id) {
          closePopUp()
        }
      }
    }
  ])

  const closeMenuAndFocusToggle = () => {
    setMenuOpen(() => false);
    dropdownRef.current.querySelector('#download_dropdown_toggle').focus();
  }

  // A chosen item is hidden with the menu, so a keyboard user's focus goes back
  // to the toggle instead of falling to the top of the page. A click made with
  // Enter / Space has event.detail 0; a mouse click leaves focus alone, so the
  // toggle does not show its focus border behind the popup.
  const closeMenuAfterChoice = (event) => {
    if (event.detail === 0) {
      closeMenuAndFocusToggle();
    } else {
      setMenuOpen(() => false);
    }
  }

  const selectFromMenu = (handler) => (e) => {
    closeMenuAfterChoice(e);
    handler(e);
  }

  // Tabbing out of the open menu closes it. A blur with no relatedTarget (a click,
  // which Safari does not focus) is left to the outside-pointerdown handler, so
  // the menu is still open when the clicked item's click event arrives.
  const closeMenuWhenFocusLeaves = (event) => {
    if (event.relatedTarget && !dropdownRef.current.contains(event.relatedTarget)) {
      setMenuOpen(false);
    }
  };

  const onMenuKeyDown = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') { return; }
    event.preventDefault();
    const items = Array.from(dropdownRef.current?.querySelectorAll('[role="menuitem"]:not([disabled])') || []);
    if (items.length === 0) { return; }
    const current = items.indexOf(document.activeElement);
    const step = event.key === 'ArrowDown' ? 1 : -1;
    items[(current + step + items.length) % items.length].focus();
  };

  useEffect(() => {
    if (!menuOpen) { return undefined; }
    dropdownRef.current?.querySelector('[role="menuitem"]:not([disabled])')?.focus();
    const onPointerDownOutside = (event) => {
      if (!dropdownRef.current?.contains(event.target)) { setMenuOpen(false); }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenuAndFocusToggle();
      }
    };
    document.addEventListener('pointerdown', onPointerDownOutside, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDownOutside, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  return (<>
    <div className="downloadButtonscontainer with-menu-tab-buttons">

      <MenuTabButtons />

      <div className="inner">
        <div className="child download-dropdown" ref={dropdownRef} onBlur={closeMenuWhenFocusLeaves}>
          <button
            type="button"
            id="download_dropdown_toggle"
            className="btn btn-default btn-lg download-btn"
            onClick={() => setMenuOpen((open) => !open)}
            disabled={redrawInProgress}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <img className="downloadIcons" src={window.dxf_svg_and_pdf_icon} alt="" /> {t('models.download')}
            <svg className="download-dropdown-caret" width="12" height="12" viewBox="0 0 10 10" aria-hidden="true">
              <path d="M2 3.5 L5 6.5 L8 3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <ul className="download-dropdown-menu" role="menu" aria-label={t('models.download')} onKeyDown={onMenuKeyDown} hidden={!menuOpen}>
            <li role="none">
              <button type="button" role="menuitem" id="download_as_dxf" className="download-dropdown-item" onClick={selectFromMenu(handleDownloadDXF)} disabled={redrawInProgress}>
                <img className="download-dropdown-item-icon" src={window.dxf_icon} alt="" /> {t('models.download_as_dxf')}
              </button>
            </li>
            <li role="none">
              <button type="button" role="menuitem" id="download_as_svg" className="download-dropdown-item" onClick={selectFromMenu(handleDownloadSVG)} disabled={redrawInProgress}>
                <img className="download-dropdown-item-icon" src={window.svg_icon} alt="" /> {t('models.download_as_svg')}
              </button>
            </li>
            <li role="none">
              <button type="button" role="menuitem" id="download_as_pdf" className="download-dropdown-item" onClick={selectFromMenu(handleDownloadPDF)} disabled={redrawInProgress}>
                <img className="download-dropdown-item-icon" src={window.pdf_icon} alt="" /> {t('models.download_as_pdf')}
              </button>
            </li>
            <MyDownloadsButton id={id} drawing={drawing} asMenuItem={true} onSelect={closeMenuAfterChoice} />
          </ul>
        </div>
      </div>
    </div>

     {showPopUp && popupContent()}
     <br />
     <br />
    </>
  );
}

export default LoggedInCanDownload;
